import { Pie, PieChart, Cell, ResponsiveContainer } from 'recharts';
import { IoIosTrendingUp, IoIosTrendingDown } from 'react-icons/io';
import { Chip } from '@/components/Chip';
import { Skeleton } from '@/components/ui/skeleton';
import { chartColors, chartColorArray } from '@/lib/colors';
import { CurrencyValue } from '@/components/CurrencyValue';
import type { TotalSpendResponse } from '@/data/types/dashboard.types';
import { DashboardEmptyState } from '@/components/Dashboard/EmptyState';

// Chart color mapping for providers (keys must be normalized: first char uppercase, rest lowercase)
const PROVIDER_COLORS: Record<string, string> = {
  Openai: chartColors.purple,
  Gemini: chartColors.teal,
  Claude: chartColors.orange,
  Anthropic: chartColors.orange,
  Google: chartColors.teal,
};

const getProviderColor = (provider: string, index: number): string => {
  const normalizedProvider = provider.charAt(0).toUpperCase() + provider.slice(1).toLowerCase();
  if (PROVIDER_COLORS[normalizedProvider]) {
    return PROVIDER_COLORS[normalizedProvider];
  }
  // Fallback to global chart color array (10 distinct colors) for unknown providers
  return chartColorArray[index % chartColorArray.length];
};

// Custom scrollbar styles - hidden by default, visible on hover
const scrollbarStyles = `
  .thin-scrollbar::-webkit-scrollbar {
    height: 3px;
  }
  .thin-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }
  .thin-scrollbar::-webkit-scrollbar-thumb {
    background: transparent;
    border-radius: 3px;
  }
  .thin-scrollbar:hover::-webkit-scrollbar-thumb {
    background: #D1D5DB;
  }
  .thin-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #9CA3AF;
  }
`;

interface TotalSpendProps {
  data?: TotalSpendResponse | null;
  isLoading?: boolean;
  isAnimationActive?: boolean;
}

const TotalSpend = ({
  data,
  isLoading = false,
  isAnimationActive = true,
}: TotalSpendProps) => {
  // Transform provider breakdown for chart data
  const chartData = data?.provider_breakdown.map((p, index) => ({
    name: p.provider,
    value: p.cost,
    fill: getProviderColor(p.provider, index),
  })) || [];

  // Transform provider breakdown for legend
  const legendItems = data?.provider_breakdown.map((p, index) => ({
    name: p.provider,
    color: getProviderColor(p.provider, index),
    value: p.cost,
    percentage: p.percentage,
  })) || [];

  // Transform modality breakdown for stat items
  const statItems = data?.modality_breakdown
    .filter(m => m.current_period_cost > 0 || m.previous_period_cost > 0)
    .map(m => ({
      label: m.modality.charAt(0).toUpperCase() + m.modality.slice(1),
      value: m.current_period_cost,
      trend: m.percentage_change,
    })) || [];

  const currentMonthTotal = data?.current_period_cost || 0;
  const previousMonthTotal = data?.previous_period_cost || 0;
  const trendPercentage = data?.percentage_change || 0;
  const isPositiveTrend = trendPercentage >= 0;
  const legendTotal = legendItems.reduce((sum, item) => sum + item.value, 0);

  if (isLoading) {
    return (
      <div className="flex flex-col -my-5">
        {/* Loading skeleton for metrics row */}
        <div className="py-4 border-b border-border">
          <div className="flex items-center justify-center gap-5 px-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex flex-col gap-1">
                <Skeleton className="h-3 w-12" />
                <Skeleton className="h-4 w-20" />
              </div>
            ))}
          </div>
        </div>
        {/* Loading skeleton for chart */}
        <div className="flex flex-col justify-center py-5">
          <div className="relative h-[230px] flex items-center justify-center">
            <Skeleton className="h-[180px] w-[180px] rounded-full" />
          </div>
          {/* Loading skeleton for legend */}
          <div className="mt-1">
            <div className="flex items-start justify-center gap-6 px-3 py-1">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex flex-col gap-0.5">
                  <Skeleton className="h-3 w-16" />
                  <Skeleton className="h-4 w-24" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check if all values are effectively zero/empty
  const hasData = data && (
    data.current_period_cost > 0 ||
    data.previous_period_cost > 0 ||
    data.provider_breakdown.some(p => p.cost > 0)
  );

  // Empty state when no data
  if (!hasData) {
    return <DashboardEmptyState height={300} />;
  }

  return (
    <>
      <style>{scrollbarStyles}</style>
      <div className="flex flex-col -my-5">
        {/* Top metrics row - horizontally scrollable */}
        <div className="relative py-4 border-b border-border">
          {/* Scroll fade indicators */}
          <div className="pointer-events-none absolute left-0 top-4 bottom-4 w-4 bg-gradient-to-r from-white to-transparent z-10" />
          <div className="pointer-events-none absolute right-0 top-4 bottom-4 w-4 bg-gradient-to-l from-white to-transparent z-10" />
          
          {/* Scrollable container */}
          <div className="overflow-x-auto overflow-y-hidden thin-scrollbar">
            <div className="flex items-center justify-center gap-5 min-w-max px-3">
              {statItems.map((item) => (
                <MetricItem
                  key={item.label}
                  label={item.label}
                  value={item.value}
                  trend={item.trend}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Chart Section */}
        <div className="flex flex-col justify-center py-5">
          {/* Gauge Chart with center content */}
          <div className="relative h-[230px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  startAngle={200}
                  endAngle={-20}
                  innerRadius="110%"
                  outerRadius="130%"
                  cornerRadius={5}
                  paddingAngle={2}
                  cx="50%"
                  cy="70%"
                  dataKey="value"
                  isAnimationActive={isAnimationActive}
                  stroke="none"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>

            {/* Center content */}
            <div className="absolute left-0 top-0 bottom-0 w-[100%] flex flex-col items-center justify-center pt-6">
              {/* Total Spend label */}
              <span className="text-[12px] text-muted-foreground font-medium text-center">
                Total Spend
              </span>

              {/* Amount */}
              <CurrencyValue value={currentMonthTotal} className="text-3xl font-bold text-foreground" />

              {/* vs last month label */}
              <span className="text-xs text-muted-foreground mt-1">
                vs. last period
              </span>

              {/* Previous month value with trend */}
              <div className="mt-1 flex items-center gap-1.5">
                <CurrencyValue value={previousMonthTotal} className="text-sm font-semibold text-foreground" />
                <Chip
                  label={`${isPositiveTrend ? '+' : ''}${trendPercentage.toFixed(1)}%`}
                  icon={isPositiveTrend 
                    ? <IoIosTrendingUp className="w-3 h-3" style={{ color: '#2ECC71' }} />
                    : <IoIosTrendingDown className="w-3 h-3" style={{ color: '#E74C3C' }} />
                  }
                  className="h-[18px] text-[9px] px-1.5 max-w-none"
                />
              </div>
            </div>
          </div>

          {/* Legend - horizontally scrollable */}
          <div className="relative mt-1">
            {/* Scroll fade indicators */}
            <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-4 bg-gradient-to-r from-white to-transparent z-10" />
            <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-4 bg-gradient-to-l from-white to-transparent z-10" />
            
            {/* Scrollable container */}
            <div className="overflow-x-auto overflow-y-hidden thin-scrollbar">
              <div className="flex items-start justify-center gap-6 min-w-max px-3 py-1">
                {legendItems.map((item) => {
                  const percentage = legendTotal > 0 
                    ? ((item.value / legendTotal) * 100).toFixed(1)
                    : '0.0';
                  return (
                    <div key={item.name} className="flex flex-col gap-0.5">
                      <div className="flex items-center gap-1.5">
                        <span
                          className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: item.color }}
                        />
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {item.name}
                        </span>
                      </div>
                      <span className="text-sm font-semibold text-foreground whitespace-nowrap">
                        <CurrencyValue value={item.value} /> <span className="text-xs font-normal text-muted-foreground">({percentage}%)</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

interface MetricItemProps {
  label: string;
  value: number;
  trend: number;
}

const MetricItem = ({ label, value, trend }: MetricItemProps) => {
  const isPositive = trend >= 0;
  const TrendIcon = isPositive ? IoIosTrendingUp : IoIosTrendingDown;
  const trendColor = isPositive ? '#2ECC71' : '#E74C3C';

  return (
    <div className="flex flex-col gap-1">
      <span className="text-[12px] text-muted-foreground leading-none">{label}</span>
      <div className="flex items-center gap-1.5">
        <CurrencyValue value={value} className="text-sm font-semibold text-foreground leading-none whitespace-nowrap" />
        <Chip
          label={`${isPositive ? '+' : ''}${trend.toFixed(1)}%`}
          icon={<TrendIcon className="w-3 h-3" style={{ color: trendColor }} />}
          className="h-[18px] text-[9px] px-1.5 max-w-none"
        />
      </div>
    </div>
  );
};

interface StatItemProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
}

const StatItem = ({ icon: Icon, label, value }: StatItemProps) => {
  return (
    <div className="flex items-center gap-3">
      <div className="w-11 h-11 rounded-sm bg-muted flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-muted-foreground" />
      </div>
      <div className="flex flex-col">
        <span className="text-sm text-muted-foreground">{label}</span>
        <span className="text-lg font-bold text-foreground">{value}</span>
      </div>
    </div>
  );
};

export { TotalSpend, StatItem };
